/**
 * The arithmetic under the art controls, kept pure so it can be tested without a GPU.
 *
 * Ported from World of Spite's loading screen, where it first drew a burning planet turning in a
 * summoning circle. None of it knows about controls or clocks: a globe's grid line is a function
 * of the time and the tilt, and "which half of an orbit is behind the planet" is a sign test.
 */
import type { Point } from "../../common/geometry.js";

export const TURN = Math.PI * 2;

/** A fixed scatter, the same every time for the same seed, so nothing jumps between frames. */
export function scatter(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Smoothstep over 0..1, clamped. */
export function smooth(t: number): number {
  const c = Math.min(1, Math.max(0, t));
  return c * c * (3 - 2 * c);
}

/** How far through `[start, start + length]` the value `t` is, eased; 0 before, 1 after. */
export function phaseIn(t: number, start: number, length: number): number {
  return length <= 0 ? (t >= start ? 1 : 0) : smooth((t - start) / length);
}

/** A point projected off the unit sphere: x right, y down, z toward the viewer. */
export interface Projected {
  x: number;
  y: number;
  z: number;
}

/**
 * A point at `lat`, `lon` on a globe turned `spin` about its axis, its north pole tipped `pitch`
 * toward the viewer, and the whole rolled `roll` in the plane of the screen.
 */
export function onSphere(lat: number, lon: number, spin: number, pitch: number, roll: number): Projected {
  const l = lon + spin;
  const x = Math.cos(lat) * Math.sin(l);
  const y = Math.sin(lat);
  const z = Math.cos(lat) * Math.cos(l);
  const y1 = y * Math.cos(pitch) - z * Math.sin(pitch);
  const z1 = y * Math.sin(pitch) + z * Math.cos(pitch);
  return rolled(x, y1, z1, roll);
}

export function rolled(x: number, y: number, z: number, roll: number): Projected {
  return {
    x: x * Math.cos(roll) - y * Math.sin(roll),
    y: -(x * Math.sin(roll) + y * Math.cos(roll)),
    z,
  };
}

/** The runs of a path on the near side (z ≥ 0), or the far side, each long enough to draw. */
export function visibleRuns<P extends Projected>(points: readonly P[], near = true): P[][] {
  const runs: P[][] = [];
  let run: P[] = [];
  for (const p of points) {
    if (p.z >= 0 === near) {
      run.push(p);
    } else {
      if (run.length > 1) runs.push(run);
      run = [];
    }
  }
  if (run.length > 1) runs.push(run);
  return runs;
}

/** A circular orbit tipped `incline` toward the viewer and turned `node` on the screen. */
export function orbitPoint(incline: number, node: number, angle: number): Projected {
  const x = Math.cos(angle);
  const y = Math.sin(angle) * Math.cos(incline);
  const z = Math.sin(angle) * Math.sin(incline);
  return rolled(x, y, z, node);
}

/** Seconds since a mote at `angle`, moving at `speed` rad/s, last passed the front of its orbit. */
export function sinceStrike(angle: number, speed: number): number {
  if (speed === 0) return Infinity;
  const past = Math.sign(speed) * (angle - Math.PI / 2);
  return (((past % TURN) + TURN) % TURN) / Math.abs(speed);
}

/** The vertices of a regular polygon, clockwise from twelve o'clock. */
export function polygon(center: Point, radius: number, sides: number, turn = 0): Point[] {
  return Array.from({ length: sides }, (_, i) => {
    const a = (i / sides) * TURN + turn;
    return { x: center.x + Math.sin(a) * radius, y: center.y - Math.cos(a) * radius };
  });
}

/**
 * The strokes of a star polygon {n/k}: vertex j to vertex j + k, `n` strokes in all when n and k
 * share no factor. Returned in drawing order, which is what lets a star draw itself stroke by
 * stroke.
 */
export function starStrokes(points: number, step: number): [number, number][] {
  const n = Math.max(3, Math.floor(points));
  const k = Math.max(1, Math.floor(step)) % n || 1;
  const strokes: [number, number][] = [];
  const seen = new Set<string>();
  for (let start = 0; start < n && strokes.length < n; start++) {
    let at = start;
    for (let i = 0; i < n; i++) {
      const next = (at + k) % n;
      const key = at < next ? `${at}-${next}` : `${next}-${at}`;
      if (seen.has(key)) break;
      seen.add(key);
      strokes.push([at, next]);
      at = next;
    }
  }
  return strokes;
}

/** A rune: two or three strokes between points of a 3 × 3 grid, in a cell from −1 to 1. */
export function makeRunes(count: number, seed: number): { strokes: [Point, Point][]; phase: number }[] {
  const random = scatter(seed);
  return Array.from({ length: Math.max(0, Math.floor(count)) }, () => {
    const node = (): Point => ({ x: Math.floor(random() * 3) - 1, y: Math.floor(random() * 3) - 1 });
    const strokes: [Point, Point][] = [];
    const want = 2 + Math.floor(random() * 2);
    while (strokes.length < want) {
      const a = node();
      const b = node();
      if (a.x !== b.x || a.y !== b.y) strokes.push([a, b]);
    }
    return { strokes, phase: random() * TURN };
  });
}

/**
 * Where a marquee's strip is drawn: the offset of the first copy, in 0..-period, after `t` seconds
 * at `speed` pixels a second. Negative speed runs the other way.
 */
export function marqueeOffset(t: number, speed: number, period: number): number {
  if (period <= 0) return 0;
  const traveled = (t * speed) % period;
  const o = -traveled;
  return o > 0 ? o - period : o <= -period ? o + period : o;
}
